import { CurrencyOption, SettingsNode } from "./api";


export type SettingsValueType = "GROUP" | "ENABLED_DISABLED_GROUP" | "ENABLED_DISABLED" | "MONEY";

export interface IBackendSettings extends SettingsNode {
	ID: string;
	Name: string;
	Type: SettingsValueType
	Values: IBackendSettings[],
	Value?: string | number;
}

const ListOfKnownBackendSettings =
	[
		"system.currency",
		"system.maxVolume",
		"system.defaultVolume",
		"play.malfunctionmessage",
		"play.translations.defaultTranslation",
		"play.translations.english",
		"play.translations.spanish",
		"play.translations.romanian",
		"play.translations.portuguese",
		"play.speedtype",
		"play.celebration",
		"play.doubleuplimit",
		"DoubleUpLimit",
		"accounting.cashierEnabled",
	] as const

type KnownSettingsKey = (typeof ListOfKnownBackendSettings)[number];

export class BackendSettings {
	protected settings: IBackendSettings;
	protected parsedSettings: Map<string, IBackendSettings> = new Map<string, IBackendSettings>();

	public constructor(settings: IBackendSettings) {
		this.settings = settings;

		let settingsToCheck:
			{
				path: string;
				settings: IBackendSettings;
			}[] = [];

		(settings.Values ?? []).forEach(v => settingsToCheck.push({
			path: v.ID,
			settings: v
		}));

		for (let i = 0; i < settingsToCheck.length; i++) {
			// add child settings
			if (settingsToCheck[i].settings.Values) {
				for (let j = 0; j < settingsToCheck[i].settings.Values.length; j++) {
					if (settingsToCheck[i].settings.Values[j]) {
						try {
							settingsToCheck.push({
								path: settingsToCheck[i].path + "." + settingsToCheck[i].settings.Values[j].ID,
								settings: settingsToCheck[i].settings.Values[j]
							});
						}
						catch (e) {
						}
					}
				}
			}
		}

		settingsToCheck.forEach(s => {
			this.parsedSettings.set(s.path, s.settings);
			// IDs supplied by the server are not consistently cased (for example,
			// DoubleUpLimit from appsettings versus play.doubleuplimit). Keep a
			// normalized lookup key so both representations resolve to the live value.
			this.parsedSettings.set(s.path.toLowerCase(), s.settings);
		});

	}

	public GetBool(path: KnownSettingsKey, defaultValue: boolean = false): boolean {
		let s = this.parsedSettings.get(path);
		if (s && s.Value) {
			return s.Value == "1";
		}

		return defaultValue;
		//throw `No such settings value: ${path}`;
	}

	public GetNum(path: KnownSettingsKey | string, defaultValue: number): number {
		let s = this.parsedSettings.get(path) ?? this.parsedSettings.get(path.toLowerCase());
		if (s && s.Value !== undefined && s.Value !== null && s.Value !== "") {
			let n = Number(s.Value);
			if (isNaN(n)) {
				return defaultValue;
			}

			return n;
		}

		return defaultValue;
	}

	public GetString(key: KnownSettingsKey, defaultValue?: string): string {
		let s = this.parsedSettings.get(key);
		if (s && s.Value) {
			if (typeof s.Value == "string") {
				return s.Value;
			}
		}

		return defaultValue;
	}

	public GetEnum<T>(key: KnownSettingsKey, defaultValue?: T): T {
		let s = this.parsedSettings.get(key);
		if (s && s.Value) {
			return s.Value as any as T;
		}

		return defaultValue;
	}

	public GetCurrency(key: KnownSettingsKey, defaultValue: CurrencyOption): CurrencyOption {
		let strVal = this.GetString(key);
		if (!strVal) {
			return defaultValue;
		}

		let val: CurrencyOption = JSON.parse(strVal);
		return val;
	}
}

/**
 * Builds the settings tree from load `Data.Settings` so GetNum can resolve
 * keys such as DoubleUpLimit and play.doubleuplimit.
 */
export function CreateBackendSettingsFromLoad(settings: SettingsNode | undefined | null): BackendSettings | undefined {
	if (!settings) {
		return undefined;
	}

	const typed = settings as IBackendSettings;
	return new BackendSettings({
		ID: typed.ID ?? "settings",
		Name: typed.Name ?? "Settings",
		Type: (typed.Type as SettingsValueType) ?? "GROUP",
		Values: typed.Values ?? [],
		Value: typed.Value,
	});
}
